import { tKey, type I18nKey } from "@/core/i18n";
import { cn } from "@/lib/utils";
import { useGetMembersQuery } from "@/modules/account/services/organizationApi";
import { Button } from "@/modules/shadcn/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modules/shadcn/ui/dialog";
import { Label } from "@repo/schema";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DEFAULT_SORT } from "../../hooks/useLabelUrlState";

export type AnnotationFilter = "all" | "true" | "false";

/**
 * Awaiting review: the image holds staged auto-annotations nobody has looked
 * at yet. A separate dimension from `annotated` rather than a third value of
 * it — staged predictions are not annotations, so such a task is still TODO.
 */
export type ReviewFilter = "all" | "true" | "false";

export interface TaskSortState {
  sortBy:
    | "createdAt"
    | "updatedAt"
    | "meanConfidence"
    | "meanIou"
    | "precision"
    | "recall";
  sortOrder: "asc" | "desc";
}

export interface TaskFilterState {
  annotationFilter: AnnotationFilter;
  labelIds: number[];
  updatedBy: number[];
  reviewFilter: ReviewFilter;
}

export interface TaskSortFilterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sort: TaskSortState;
  filter: TaskFilterState;
  labels: Label[];
  metricsAvailable?: boolean;
  onApply: (next: { sort: TaskSortState; filter: TaskFilterState }) => void;
}

interface SortOption {
  value: TaskSortState["sortBy"];
  label: I18nKey<"label">;
}

const DATE_SORT_OPTIONS: SortOption[] = [
  { value: "createdAt", label: tKey("label:sortFilter.fields.createdAt") },
  { value: "updatedAt", label: tKey("label:sortFilter.fields.updatedAt") },
];

const METRIC_SORT_OPTIONS: SortOption[] = [
  { value: "meanConfidence", label: tKey("label:sortFilter.fields.meanConfidence") },
  { value: "meanIou", label: tKey("label:sortFilter.fields.meanIou") },
  { value: "precision", label: tKey("label:sortFilter.fields.precision") },
  { value: "recall", label: tKey("label:sortFilter.fields.recall") },
];

const METRIC_SORT_KEYS = new Set<TaskSortState["sortBy"]>([
  "meanConfidence",
  "meanIou",
  "precision",
  "recall",
]);

export const isMetricSort = (key: TaskSortState["sortBy"]): boolean =>
  METRIC_SORT_KEYS.has(key);

const filterToCheckboxes = (filter: AnnotationFilter | ReviewFilter) => ({
  yes: filter === "all" || filter === "true",
  no: filter === "all" || filter === "false",
});

const checkboxesToFilter = (yes: boolean, no: boolean): AnnotationFilter => {
  if (yes && no) return "all";
  if (yes) return "true";
  if (no) return "false";
  return "all";
};

const toggle = (set: Set<number>, id: number) => {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
};

/**
 * Sort and filter for the data source list, in a single modal — the two used to
 * be separate dialogs behind separate toolbar buttons. Both halves are staged
 * locally and committed together by "Apply", so one navigation carries the whole
 * query state (see `setSortAndFilter`).
 */
export const TaskSortFilterDialog = ({
  open,
  onOpenChange,
  sort,
  filter,
  labels,
  metricsAvailable = false,
  onApply,
}: TaskSortFilterDialogProps) => {
  const { t } = useTranslation(["label", "common"]);
  const [sortBy, setSortBy] = useState<TaskSortState["sortBy"]>(sort.sortBy);
  const [sortOrder, setSortOrder] = useState<TaskSortState["sortOrder"]>(
    sort.sortOrder,
  );
  const initialAnnotated = filterToCheckboxes(filter.annotationFilter);
  const [annotatedYes, setAnnotatedYes] = useState(initialAnnotated.yes);
  const [annotatedNo, setAnnotatedNo] = useState(initialAnnotated.no);
  const [labelIds, setLabelIds] = useState<Set<number>>(
    () => new Set(filter.labelIds),
  );
  const [updatedBy, setUpdatedBy] = useState<Set<number>>(
    () => new Set(filter.updatedBy),
  );
  const initialReview = filterToCheckboxes(filter.reviewFilter);
  const [reviewYes, setReviewYes] = useState(initialReview.yes);
  const [reviewNo, setReviewNo] = useState(initialReview.no);

  const { data: members = [] } = useGetMembersQuery();

  useEffect(() => {
    if (!open) return;
    // If the URL has a metric sort but metrics are no longer available (e.g. report
    // was re-run), fall back to the default so no invisible pill is "selected".
    setSortBy(
      isMetricSort(sort.sortBy) && !metricsAvailable
        ? DEFAULT_SORT.sortBy
        : sort.sortBy,
    );
    setSortOrder(sort.sortOrder);
    const annotated = filterToCheckboxes(filter.annotationFilter);
    setAnnotatedYes(annotated.yes);
    setAnnotatedNo(annotated.no);
    setLabelIds(new Set(filter.labelIds));
    setUpdatedBy(new Set(filter.updatedBy));
    const review = filterToCheckboxes(filter.reviewFilter);
    setReviewYes(review.yes);
    setReviewNo(review.no);
  }, [open, sort, filter, metricsAvailable]);

  const handleSortByChange = (value: TaskSortState["sortBy"]) => {
    setSortBy(value);
    // Default to worst-first (asc) for metrics, newest-first (desc) for dates.
    setSortOrder(isMetricSort(value) ? "asc" : "desc");
  };

  const handleApply = () => {
    onApply({
      sort: { sortBy, sortOrder },
      filter: {
        annotationFilter: checkboxesToFilter(annotatedYes, annotatedNo),
        labelIds: [...labelIds],
        updatedBy: [...updatedBy],
        reviewFilter: checkboxesToFilter(reviewYes, reviewNo),
      },
    });
    onOpenChange(false);
  };

  const handleReset = () => {
    setSortBy(DEFAULT_SORT.sortBy);
    setSortOrder(DEFAULT_SORT.sortOrder);
    setAnnotatedYes(true);
    setAnnotatedNo(true);
    setLabelIds(new Set());
    setUpdatedBy(new Set());
    setReviewYes(true);
    setReviewNo(true);
  };

  const isMetric = isMetricSort(sortBy);
  const sortOptions = metricsAvailable
    ? [...DATE_SORT_OPTIONS, ...METRIC_SORT_OPTIONS]
    : DATE_SORT_OPTIONS;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[calc(100vh-48px)] flex-col gap-0 p-0 sm:max-w-[560px]">
        <DialogHeader className="shrink-0 border-b border-black/10 px-5 py-4">
          <DialogTitle className="text-lg font-bold tracking-[-0.01em]">
            {t("sortFilter.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-5">
          <GroupHeading>{t("sortFilter.sort")}</GroupHeading>

          <Field label={t("sortFilter.field")}>
            {sortOptions.map((opt) => (
              <Pill
                key={opt.value}
                label={t(opt.label)}
                selected={sortBy === opt.value}
                onClick={() => handleSortByChange(opt.value)}
              />
            ))}
          </Field>

          <Field label={t("sortFilter.direction")}>
            <Pill
              label={isMetric ? t("sortFilter.highestFirst") : t("sortFilter.newestFirst")}
              selected={sortOrder === "desc"}
              onClick={() => setSortOrder("desc")}
            />
            <Pill
              label={isMetric ? t("sortFilter.lowestFirst") : t("sortFilter.oldestFirst")}
              selected={sortOrder === "asc"}
              onClick={() => setSortOrder("asc")}
            />
          </Field>

          <div className="h-px shrink-0 bg-black/10" />
          <GroupHeading>{t("sortFilter.filter")}</GroupHeading>

          <Field label={t("sortFilter.annotated")}>
            <Pill
              label={t("sortFilter.yes")}
              selected={annotatedYes}
              onClick={() => setAnnotatedYes((v) => !v)}
            />
            <Pill
              label={t("sortFilter.no")}
              selected={annotatedNo}
              onClick={() => setAnnotatedNo((v) => !v)}
            />
          </Field>

          <Field label={t("sortFilter.awaitingReview")}>
            <Pill
              label={t("sortFilter.yes")}
              selected={reviewYes}
              onClick={() => setReviewYes((v) => !v)}
            />
            <Pill
              label={t("sortFilter.no")}
              selected={reviewNo}
              onClick={() => setReviewNo((v) => !v)}
            />
          </Field>

          {labels.length > 0 && (
            <Field label={t("sortFilter.label")}>
              {labels.map((label) => (
                <Pill
                  key={label.id}
                  label={label.name}
                  swatch={label.color}
                  selected={labelIds.has(label.id)}
                  onClick={() => setLabelIds((prev) => toggle(prev, label.id))}
                />
              ))}
            </Field>
          )}

          {members.length > 0 && (
            <Field label={t("sortFilter.updatedBy")}>
              {members.map((member) => (
                <Pill
                  key={member.user.id}
                  label={member.user.fullName}
                  selected={updatedBy.has(member.user.id)}
                  onClick={() =>
                    setUpdatedBy((prev) => toggle(prev, member.user.id))
                  }
                />
              ))}
            </Field>
          )}
        </div>

        <DialogFooter className="shrink-0 flex-row items-center justify-between border-t border-black/10 px-5 py-3.5 sm:justify-between">
          <Button variant="ghost" size="sm" onClick={handleReset}>
            {t("sortFilter.resetAll")}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t("common:actions.cancel")}
            </Button>
            <Button onClick={handleApply}>{t("common:actions.apply")}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const GroupHeading = ({ children }: { children: ReactNode }) => (
  <h3 className="text-[11px] font-bold uppercase leading-4 tracking-[0.08em] text-muted-foreground">
    {children}
  </h3>
);

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex flex-col gap-2">
    <span className="text-xs font-medium leading-4 text-foreground/60">
      {label}
    </span>
    <div className="flex flex-wrap gap-1.5">{children}</div>
  </div>
);

interface PillProps {
  label: string;
  selected: boolean;
  onClick: () => void;
  /** Hex colour rendered as a small square in front of the label. */
  swatch?: string;
}

const Pill = ({ label, selected, onClick, swatch }: PillProps) => (
  <button
    type="button"
    aria-pressed={selected}
    onClick={onClick}
    className={cn(
      "flex h-[30px] cursor-pointer items-center gap-[7px] rounded-full border px-3 text-[13px] font-medium leading-[18px] transition-colors",
      selected
        ? "border-emerald-500 bg-emerald-400/15 text-emerald-700"
        : "border-black/10 bg-background text-foreground/75 hover:bg-black/[0.03] hover:text-foreground",
    )}
  >
    {swatch && (
      <span
        className="block size-2 shrink-0 rounded-[2px]"
        style={{ backgroundColor: swatch }}
      />
    )}
    {label}
  </button>
);
